import * as APICommands from "../APICommands/index.js";
import { MasterIndex } from "./AMasterIndex.js";
import { ModelBase } from "./ModelBase.js";
import { SoilLayer } from "./soilLayer.js";
import { geometry } from "gardenspadejs";
import { IrrigationSet } from "./Scheduling/IrrigationSet.js";
import { VertexInfo } from "verdi-common/schemas/commonSubSchemas/vertexInfo";
import { IrrigationDeviceBase } from "./Devices/IrrigationDeviceBase.js";
import { SensoterraMoistureSensor } from "./Devices/SensoterraMoistureSensor.js";
import { ThirdPartyDeviceBase } from "./Devices/ThirdPartyDeviceBase.js";
import { ThirdPartyDevice } from "verdi-common/schemas/thirdPartyDevice/thirdPartyDevice";
import { Zone as ZoneBaseType } from "verdi-common/schemas/zone/zone";
import { ZoneMetadata } from "verdi-common/schemas/zone/subSchemas/zoneMetadata";
import { ResponseTimeStats } from "verdi-common/schemas/responseTimeStats/responseTimeStats";

export class Zone extends ModelBase {
    /**
     *
     */
    defaultRelativeIrrigationDuration: number;

    responseTimeStats?: ResponseTimeStats;

    /**
     *
     */
    recommendedRelativeIrrigationDuration: number;

    checkForRecentData = true;
    /**
     * Every vertices in coordinates for mapping purposes
     * @type {{lat, lng}[]}
     */
    polygon = [];

    /**
     * Every vertices in coordinates for mapping purposes
     * @deprecated
     * @type {{lat, lng}[]}
     * @return {{lat, lng}[]}
     */
    get vertices() {
        return this.polygon;
    }

    /**
     * @deprecated
     * @param {{lat, lng}[]} v
     */
    set vertices(v) {
        this.polygon = v;
    }

    /**
     *
     * @type {SensoterraMoistureSensor[]}
     */
    moistureSensors = [];

    /**
     * Every irrigation set that currently lists this zone as a member.
     * Derived from MasterIndex so it stays consistent as sets are edited,
     * reloaded, or deleted.
     */
    get linkedSets(): IrrigationSet[] {
        return MasterIndex.irrigationSet.all.filter((s) => s.zoneIds?.includes(this.id));
    }

    /**
     *
     * @type {[]}
     */
    devices: (IrrigationDeviceBase | ThirdPartyDevice | ThirdPartyDeviceBase | SensoterraMoistureSensor)[] = [];
    deviceIDs: string[] = [];

    /**
     * Moisture control information
     * @type {{targetValue: number, moistureControlEnabled: boolean, moistureControlCapable: boolean}}
     */
    moistureInfo = {
        moistureControlCapable: false,
        targetValue: 0.5,
        moistureControlEnabled: false,
    };

    // TODO replace with SupplementalZoneGraphInfo after it's moved to verdi-common
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    supplementalZoneGraphInfo: any;

    userMetadata: Partial<ZoneMetadata> = new ZoneMetadata({});
    /**
     *
     * @type {SoilLayer[]}
     */
    soilLayers = [];

    /**
     * Creates a new entity of this type and sends it to the server. When the server accepts the new entity, a new local entity
     * is created and this function returns.
     * @constructor
     */
    static async createNew(polygon: VertexInfo[][], name: string): Promise<Zone> {
        let json;
        await APICommands.ZoneAPI.addOne(polygon, name).then((r) => {
            json = r;
            if (r.zone) {
                json = r.zone;
            }
        });

        return new Zone(json);
    }

    constructor(json) {
        super(json);
        this.category = "zone";
        this.dbID = json._id || json.id;
        this.loadFromJSON(json);
        this.load().catch((e) => {
            console.warn(e);
        });
    }
    loadFromJSON(json: ZoneBaseType) {
        this._lastJSON = json;
        this.name = json.name;
        this.owner = json.owner?.toString();
        this.userMetadata = Object.assign(this.userMetadata || {}, json.userMetadata || {});
        if (json.polygons) {
            this.polygon = json.polygons[0];
        }
        this.soilCapacity = json.soilCapacity;
        if (json.soilLayers && Array.isArray(json.soilLayers)) {
            this.soilLayers = json.soilLayers.map((sl) => new SoilLayer(sl));
        }
        this.defaultRelativeIrrigationDuration = json.defaultRelativeIrrigationDuration ?? 1;
        this.recommendedRelativeIrrigationDuration = json.recommendedRelativeIrrigationDuration ?? 1;
        this.supplementalZoneGraphInfo = json.supplementalZoneGraphInfo ?? {
            supplementalDataStreams: [],
        };
        this.responseTimeStats = json.responseTimeStats ?? undefined;
    }

    soilCapacity?: number;
    /**
     * @type {AreaOfInterest}
     */
    areaOfInterest;
    /**
     * Deletes this entity and returns when the entity has been deleted from the server.
     */
    async delete() {
        await APICommands.ZoneAPI.deleteOne(this.id).then(() => {
            super.delete();
        });
    }

    /**
     *
     * @param  {{lat, lng}[]} newVertices
     * @param {String} newName
     * @return {Promise<void>}
     * @param newMoistureControlInfo
     */
    async edit(
        newVertices?: { lat: number; lng: number }[],
        newName?: string,
        // TODO replace with MoistureControlInfo after it's moved to verdi-common
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        newMoistureControlInfo?: any,
    ): Promise<Zone> {
        await APICommands.ZoneAPI.editOne(this.dbID, {
            name: newName,
            // @ts-ignore
            vertices: newVertices,
            moistureControlInfo: newMoistureControlInfo,
        }).then((r) => {
            this.loadFromJSON(r);
            this.onChange.trigger({ target: this });
        });
        return this;
    }

    /**
     * Edits this entity on the server. Any changes made through this function will not affect the local instance until
     * they are updated on the server.
     *
     * @return {Promise<ModelBase>}
     */
    async argEdit(args: Partial<ZoneBaseType>): Promise<Zone> {
        await APICommands.ZoneAPI.editOne(
            this.dbID,
            // @ts-ignore
            args,
        ).then((r) => {
            this.loadFromJSON(r);
            this.onChange.trigger({ target: this });
        });
        this.onChange.trigger({ target: this, change: this });
        return this;
    }

    isPointInZone(point: number[]): boolean {
        return geometry.pointInPolygon(
            point,
            this.polygon.map((vertice) => [vertice.lat, vertice.lng]),
            undefined,
            undefined,
        );
    }
}
